import React, { useEffect, useRef, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

import useIntersectionObserver from '../hooks/useIntersectionObserver';
import ReactionBar from './ReactionBar';
import MessageStatus from './MessageStatus';
import AiToolsButton from './AiToolsButton';
import AiReplyGenerator from './AiReplyGenerator';
import LinkPreview from './LinkPreview';
import AudioPlayer from './AudioPlayer';

const ChatMessage = ({ chatId, msg, prevMessage, isUser, recipientProfile, onReact, onReply, onAnalysisComplete, onAiReply, onUpdateStatus, onEdit, onRetry, searchTerm }) => {
  const { t } = useTranslation();
  const [ref, entry] = useIntersectionObserver({ threshold: 0.5 });
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(msg.content);

  const getHighlightedText = (text, highlight) => {
    if (!highlight.trim()) {
      return <span>{text}</span>;
    }
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === highlight.toLowerCase() ? (
            <mark key={i} className="bg-yellow-400 text-black rounded-sm">{part}</mark>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  useEffect(() => {
    if (entry?.isIntersecting && msg.status !== 'read' && !isUser && msg.from !== 'system' && !msg.isHarmonySync && !msg.isGift) {
      onUpdateStatus(msg.id, 'read');
    }
  }, [entry?.isIntersecting, msg.id, msg.status, isUser, onUpdateStatus, msg.from, msg.isHarmonySync, msg.isGift]);

  const handleEdit = () => {
    if (editedContent.trim() !== msg.content) {
      onEdit(msg.id, editedContent.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedContent(msg.content);
    setIsEditing(false);
  };

  const showAvatar = !prevMessage || prevMessage.from !== msg.from || prevMessage.isGift || prevMessage.isHarmonySync;
  const canEdit = isUser && msg.timestamp && (Date.now() - (typeof msg.timestamp.toDate === 'function' ? msg.timestamp.toDate() : new Date(msg.timestamp)).getTime()) < 5 * 60 * 1000; // 5 minute edit window

  if (msg.isHarmonySync) {
    return (
      <div ref={ref} key={msg.id} className="text-center text-xs text-purple-300 bg-purple-900/30 rounded-full py-1.5 px-4 my-4 flex items-center justify-center gap-2 max-w-xs mx-auto">
        <LucideIcons.Sparkles size={14} /> {msg.content}
      </div>
    );
  }

  if (msg.isGift) {
    return (
      <div ref={ref} key={msg.id} className="text-center text-xs text-yellow-300 bg-yellow-900/30 rounded-full py-1.5 px-4 my-4 flex items-center justify-center gap-2 max-w-xs mx-auto">
        <LucideIcons.Gift size={12} /> {msg.content}
      </div>
    );
  }

  const timestampStr = msg.timestamp ? new Date(typeof msg.timestamp.toDate === 'function' ? msg.timestamp.toDate() : msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '';
  const bubbleShape = isUser 
    ? (showAvatar ? 'rounded-3xl rounded-br-sm' : 'rounded-3xl rounded-r-sm')
    : (showAvatar ? 'rounded-3xl rounded-bl-sm' : 'rounded-3xl rounded-l-sm');

  return (
    <div ref={ref} key={msg.id} className="group relative w-full flex flex-col">
      <div className={`flex items-end gap-2 w-full ${isUser ? 'flex-row-reverse' : 'flex-row'} ${!showAvatar ? 'mt-1' : 'mt-4'}`}>
        <div className="w-8 flex-shrink-0 flex justify-center">
          {showAvatar && (
            <img
              src={(isUser ? recipientProfile?.photoURL : recipientProfile?.photoURL) || "https://placehold.co/32x32/AEC6CF/FFFFFF?text=U"}
              alt="avatar"
              className="w-8 h-8 rounded-full object-cover shadow-sm border border-gray-700"
            />
          )}
        </div>
        <div className={`relative max-w-[85%] md:max-w-[70%] ${bubbleShape} ${isUser ? 'bg-gradient-to-br from-indigo-600 to-blue-700 text-white shadow-lg shadow-indigo-500/20' : 'bg-gray-800/90 backdrop-blur-md text-gray-100 shadow-sm border border-gray-700/50'}`}>
          {msg.replyingTo && (
            <div className="p-2 mx-2 mt-2 mb-1 border-l-2 border-white/30 bg-black/20 rounded-lg">
              <p className="font-bold text-xs text-white/80">
                {msg.replyingTo.from === recipientProfile?.id ? recipientProfile?.displayName : t('chatMessage.you')}
              </p>
              <p className="text-xs italic truncate text-white/70">
                {msg.replyingTo.content}
              </p>
            </div>
          )}
          {isEditing ? (
            <div className="p-3">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full bg-black/30 rounded-xl p-3 text-white focus:outline-none focus:ring-2 focus:ring-white/50 resize-none min-h-[60px]"
              />
              <div className="flex justify-end gap-3 mt-2">
                <button onClick={handleCancelEdit} className="text-xs font-semibold text-white/70 hover:text-white transition-colors">{t('chatMessage.cancel')}</button>
                <button onClick={handleEdit} className="text-xs font-bold text-white hover:text-white/80 transition-colors bg-white/20 px-3 py-1 rounded-full">{t('chatMessage.save')}</button>
              </div>
            </div>
          ) : (
          <div className="px-4 py-2.5 flex flex-col relative group">
            {msg.linkPreview && <LinkPreview preview={msg.linkPreview} />}
            {msg.imageUrl && <img src={msg.imageUrl} alt="Sent content" className="rounded-xl max-w-xs mt-1 mb-2 shadow-sm" />}
            {msg.audioUrl && <AudioPlayer src={msg.audioUrl} />}
            {msg.content && <p className="text-[15px] leading-relaxed break-words whitespace-pre-wrap">{getHighlightedText(msg.content, searchTerm)}</p>}
            
            <div className="flex items-center justify-end gap-1.5 mt-1 -mb-1 opacity-70">
                <span className="text-[10px] font-medium tracking-wide">{timestampStr}</span>
                {isUser && <MessageStatus status={msg.status} onRetry={onRetry} />}
            </div>
          </div>
          )}
        </div>
        
        {/* Actions row: shown on hover or statically placed near bubble */}
        <div className={`flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${isUser ? 'mr-2' : 'ml-2'}`}>
            {(msg.from !== 'system' && !msg.isHarmonySync && !msg.isGift) && (
            <ReactionBar chatId={chatId} message={msg} onReact={onReact} isUser={isUser} />
            )}
            <AiToolsButton 
                content={msg.content} 
                entryId={msg.id}
                authorId={msg.from}
                contextType="chatMessage"
                onAnalysisComplete={onAnalysisComplete} 
            />
            <AiReplyGenerator content={msg.content} onReplyGenerated={onAiReply} />
        </div>
      </div>
    </div>
  );
};
export default ChatMessage;
